import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebase';
import { usePostHog } from 'posthog-js/react';

const claimStravaClubCredits = httpsCallable(functions, 'claimStravaClubCredits');
const getCreditRewardStatus = httpsCallable(functions, 'getCreditRewardStatus');

function StravaClubCard({ user, onRewardClaimed }) {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [showForm, setShowForm] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [isAlreadyClaimed, setIsAlreadyClaimed] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const checkInProgressRef = useRef(false);

    const STRAVA_CLUB_URL = "https://www.strava.com/clubs/mythicmaps";
    const STORAGE_KEY = 'strava_club_join_pending';

    useEffect(() => {
        checkIfAlreadyClaimed();
        // Track card view when component mounts and user is present
        if (user && !isAlreadyClaimed) {
            posthog?.capture('strava_club_card_viewed', {
                page: 'home'
            });
        }
    }, [user]);

    useEffect(() => {
        const checkPendingJoin = async () => {
            if (!user || isAlreadyClaimed || checkInProgressRef.current) return;
            
            const pendingJoin = localStorage.getItem(STORAGE_KEY);
            if (pendingJoin === user.uid && !isChecking) {
                setShowForm(true);
                
                setTimeout(() => {
                    if (!checkInProgressRef.current && !isAlreadyClaimed) {
                        handleVerifyMembership(true);
                    }
                }, 2000);
            }
        };
        
        checkPendingJoin();
        
        const handleFocus = () => {
            checkPendingJoin();
        };
        
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkPendingJoin();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, isAlreadyClaimed, isChecking]);

    const checkIfAlreadyClaimed = async () => {
        if (!user) return;

        try {
            const result = await getCreditRewardStatus();
            if (result.data?.stravaClub?.claimed) {
                setIsAlreadyClaimed(true);
                
                if (user.uid) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error("Error checking reward status:", error);
        }
    };

    const handleJoinClubClick = () => {
        if (user?.uid) {
            localStorage.setItem(STORAGE_KEY, user.uid);
        }

        // Track club join click
        posthog?.capture('strava_club_join_clicked', {
            page: 'home'
        });

        window.open(STRAVA_CLUB_URL, '_blank');
        setShowForm(true);
    };

    const handleVerifyMembership = async (isAutoCheck = false) => {
        if (checkInProgressRef.current) {
            console.log('Verification already in progress, skipping...');
            return;
        }

        checkInProgressRef.current = true;
        setIsChecking(true);
        setError('');

        // Track verification attempt
        if (!isAutoCheck) {
            posthog?.capture('strava_club_verify_attempted', {
                page: 'home'
            });
        }

        try {
            const result = await claimStravaClubCredits();

            if (result.data.success) {
                setSuccess(true);
                setIsAlreadyClaimed(true);

                if (user?.uid) {
                    localStorage.removeItem(STORAGE_KEY);
                }

                // Track successful claim
                posthog?.capture('strava_club_credits_claimed', {
                    credits_earned: 5,
                    auto_verified: isAutoCheck,
                    page: 'home',
                    userId: user?.uid
                });

                if (onRewardClaimed) {
                    onRewardClaimed();
                }
            } else {
                setError(result.data.error || 'Could not verify membership');

                // Track verification failure
                posthog?.capture('strava_club_verify_failed', {
                    error_message: result.data.error || 'Could not verify membership',
                    auto_check: isAutoCheck,
                    page: 'home'
                });
            }
        } catch (error) {
            console.error("Error claiming Strava club credits:", error);

            let errorMessage;
            if (error.code === 'unauthenticated' && error.message.includes('Strava access expired')) {
                errorMessage = 'Your Strava connection has expired. Please reconnect your Strava account in Settings.';
            } else if (error.code === 'failed-precondition') {
                errorMessage = 'Please connect your Strava account first in Settings.';
            } else {
                errorMessage = 'Failed to verify membership. Please try again.';
            }

            setError(errorMessage);

            // Track error
            posthog?.capture('strava_club_verify_error', {
                error_code: error.code,
                error_message: error.message,
                auto_check: isAutoCheck,
                page: 'home'
            });
        } finally {
            setIsChecking(false);
            checkInProgressRef.current = false;
        }
    };

    if (isAlreadyClaimed) {
        return (
            <div className="reward-card email-reward-card claimed">
                <div className="reward-content">
                    <div className="reward-header">
                        <h4>🏃 {t('stravaClubCard.joinedClub', 'Joined the Strava club')}</h4>
                        <span className="reward-value">+5 💎</span>
                    </div>
                    <p>{t('stravaClubCard.claimedDiamonds', "You've claimed your diamonds!")}</p>
                    
                    <button className="reward-button" disabled>
                        {t('stravaClubCard.diamondsClaimed', 'Diamonds Claimed')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="reward-card email-reward-card">
            <div className="reward-content">
                <div className="reward-header">
                    <h4>🏃 {t('stravaClubCard.joinStravaClub', 'Join Strava Club')}</h4>
                    <span className="reward-value">+5 💎</span>
                </div>
                <p>{t('stravaClubCard.joinStravaClubDesc', 'Join our Strava Club and earn 5 diamonds!')}</p>

                {!showForm ? (
                    <button
                        className="reward-button"
                        onClick={handleJoinClubClick}
                    >
                        {t('stravaClubCard.joinStravaClub', 'Join Strava Club')}
                    </button>
                ) : (
                    <div style={{ marginTop: '16px' }}>
                        {success ? (
                            <div className="success-message">
                                ✓ {t('stravaClubCard.claimedDiamonds', "You've claimed your diamonds!")}
                            </div>
                        ) : (
                            <>
                                <p style={{
                                    fontSize: '14px',
                                    color: '#6b6b6b',
                                    marginBottom: '12px'
                                }}>
                                    {isChecking ? 
                                        t('stravaClubCard.checkingMembership', 'Checking your membership status...') : 
                                        t('stravaClubCard.joinedPrompt', 'Joined the club? Click below to verify your membership.')}
                                </p>

                                {error && (
                                    <div className="error-message">
                                        {error}
                                    </div>
                                )}

                                <button
                                    className="reward-button"
                                    onClick={() => handleVerifyMembership(false)}
                                    disabled={isChecking}
                                >
                                    {isChecking ? t('stravaClubCard.verifying', 'Verifying...') : t('stravaClubCard.verifyMembership', 'Verify Membership')}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StravaClubCard;

